// src/utils/theme.js
import { createTheme } from '@mui/material/styles';
import { getRoleColors } from './roleTheme';

const createAppTheme = (role = 'User') => {
  const roleColors = getRoleColors(role);

  return createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: roleColors.primary,
        light: roleColors.primaryLight,
        dark: roleColors.primaryDark,
        contrastText: roleColors.contrastText,
      },
      secondary: {
        main: '#dc004e',
        light: '#ff6b9d',
        dark: '#9a0036',
        contrastText: '#ffffff',
      },
      success: {
        main: '#00c853',
        light: '#5efc82',
        dark: '#009624',
      },
      warning: {
        main: '#ff9800',
        light: '#ffcc02',
        dark: '#c66900',
      },
      error: {
        main: '#f44336',
        light: '#ff7961',
        dark: '#ba000d',
      },
      background: {
        default: '#f5f6fa',
        paper: '#ffffff',
      },
      text: {
        primary: '#2c3e50',
        secondary: '#6c757d',
      },
      divider: '#e0e6ed',
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontWeight: 700,
        fontSize: '2.5rem',
      },
      h2: {
        fontWeight: 600,
        fontSize: '2rem',
      },
      h3: {
        fontWeight: 600,
        fontSize: '1.75rem',
      },
      h4: {
        fontWeight: 600,
        fontSize: '1.5rem',
      },
      h5: {
        fontWeight: 600,
        fontSize: '1.25rem',
      },
      h6: {
        fontWeight: 600,
        fontSize: '1rem',
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.6,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.5,
      },
      button: {
        fontWeight: 600,
        textTransform: 'none',
      },
    },
    shape: {
      borderRadius: 12,
    },
    spacing: 8,
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            borderRadius: 16,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 600,
            padding: '8px 16px',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.12)',
              transform: 'translateY(-1px)',
            },
          },
          contained: {
            background: roleColors.gradient,
            color: roleColors.contrastText,
            '&:hover': {
              background: roleColors.gradientHover,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: '#ffffff',
            borderRight: '1px solid #e0e6ed',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: '#ffffff',
            color: '#2c3e50',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            margin: '4px 8px',
            '&:hover': {
              backgroundColor: roleColors.primary,
              color: '#ffffff',
              '& .MuiListItemIcon-root': {
                color: '#ffffff',
              },
            },
            '&.Mui-selected': {
              backgroundColor: roleColors.primaryLight,
              color: '#ffffff',
              '& .MuiListItemIcon-root': {
                color: '#ffffff',
              },
              '&:hover': {
                backgroundColor: roleColors.primary,
              },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 600,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
            },
          },
        },
      },
    },
    custom: {
      roleColors,
    },
  });
};

export default createAppTheme;